import React, { useState, useRef, useEffect, useCallback, useContext } from "react";
import './Dashboard.css';
import { DataContainer } from "./DataContainer.js";
import { log } from "../utils/logger.js";
import JSZip from 'jszip';
import { setCookie, getCookie } from '../cookieUtils.js';
import { debounce } from 'lodash';
import AddToVaultSection from "../components/AddToVault.js";
import VaultSelector from "../components/VaultSelector.js";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import { AuthContext }  from '../App.js'

// Set the worker source to the locally hosted `pdf.worker.mjs`
pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.mjs";

function Database({ Ampersandposium, setAmpersandposium, userEmail, styles, isDarkMode, wait, isMobile, vaults = ['-'], setVaults = null, refreshVaults = null }) {
  const { vectorVault } = useContext(AuthContext);
  const ChatContainerRef = useRef(null); // create a reference using the useRef hook
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationShown, setNotificationShown] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [inputValue, setInputValue] = useState('demo');
  const [siteUrl, setSiteUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [customDataVisible, setCustomDataVisible] = useState(false);
  const [customData, setCustomData] = useState('');
  const [showDelete, setShowDelete] = useState(true);
  const [gpt4, setGpt4] = useState(false);
  const [display, setDisplay] = useState(true);
  const [database, setDatabase] = useState(true);
  const [reloadData, setReloadData] = useState(true);
  
  // Handle window resize
  useEffect(() => {
    function handleResize() {
      setWindowWidth(window.innerWidth);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (reloadData && !wait && Ampersandposium) {
      updateAmpersandposium(Ampersandposium);
    }
  }, [reloadData, wait, Ampersandposium]);

  // Refresh vaults every time the database is visited
  useEffect(() => {
    if (refreshVaults && !wait && userEmail) {
      refreshVaults();
    }
  }, [refreshVaults, wait, userEmail]);

  // Updated debounced updateAmpersandposium
  const updateAmpersandposium = useCallback(
    debounce((value) => {
      setAmpersandposium(value);
      setCookie("Ampersandposium", value);
      setReloadData(prev => !prev);
    }, 300),
    []
  );

  const Notification = ({ message, notificationShown, setNotificationShown }) => {
    const [isVisible, setIsVisible] = useState(true);
  
    useEffect(() => {
      if (notificationShown) {
        const timeoutId = setTimeout(() => {
          setIsVisible(false);
          setNotificationShown(false); // Reset the notificationShown state when the notification is hidden
        }, 5000);
  
        return () => {
          clearTimeout(timeoutId);
        };
      }
    }, [notificationShown, setNotificationShown]);
  
    if (!isVisible || !notificationShown) {
      return null;
    }
  
    return (
      <div style={styles.notificationStyle}>
        {message}
      </div>
    );
  };

  
  async function extractTextFromDocx(arrayBuffer) {
      const zip = new JSZip();
      const content = await zip.loadAsync(arrayBuffer);
      const docXML = await content.files["word/document.xml"].async("text");
      const parser = new DOMParser();
      const docNode = parser.parseFromString(docXML, "application/xml");

      const nsResolver = (prefix) => {
          return {
              'w': 'http://schemas.openxmlformats.org/wordprocessingml/2006/main'
          }[prefix] || null;
      };

      const textNodeResults = docNode.evaluate('//w:t', docNode, nsResolver, XPathResult.ORDERED_NODE_SNAPSHOT_TYPE, null);

      let textContentArray = [];
      for (let i = 0; i < textNodeResults.snapshotLength; i++) {
          textContentArray.push(textNodeResults.snapshotItem(i).textContent);
      }
      
      return textContentArray.join(" ");
  }
  
  async function handleCustomDataSave(customDataContent) {
      setIsLoading(true); 
        await vectorVault.addCloud({ vault: Ampersandposium, text : customDataContent })
      .catch(error => {
          console.error("There was an error adding your data to the cloud:", error);
      })
      .finally(() => {
          setIsLoading(false); // Hide spinner when the fetch is complete
          setCustomData('')
          setReloadData(prevState => !prevState); // Reload data
          alert("Data uploaded successfully.")
      });
  }

  
    async function handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return; // No file selected
      
      log('file type:', file.type);
      setIsLoading(true);
  
      try {
          let uploadResult;
          
          switch(file.type) {
              case 'text/plain': // .txt
              case 'text/csv':   // .csv
              case 'text/markdown': // .md
                  const textReader = new FileReader();
                  uploadResult = await new Promise((resolve, reject) => {
                      textReader.onload = async (e) => {
                          try {
                              const response = await vectorVault.addCloud({ 
                                  vault: Ampersandposium, 
                                  text: e.target.result, 
                                  name: file.name,
                                  split: true,
                                  split_size: 1000
                              });
                              resolve(response);
                          } catch (error) {
                              reject(error);
                          }
                      };
                      textReader.onerror = () => reject(new Error('Failed to read file'));
                      textReader.readAsText(file);
                  });
                  break;
                  
              case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': // .docx
                  const docxReader = new FileReader();
                  uploadResult = await new Promise((resolve, reject) => {
                      docxReader.onload = async (e) => {
                          try {
                              const fileContent = await extractTextFromDocx(e.target.result);
                              const response = await vectorVault.addCloud({ 
                                  vault: Ampersandposium, 
                                  text: fileContent, 
                                  name: file.name,
                                  split: true,
                                  split_size: 1000
                              });
                              resolve(response);
                          } catch (error) {
                              reject(error);
                          }
                      };
                      docxReader.onerror = () => reject(new Error('Failed to read file'));
                      docxReader.readAsArrayBuffer(file);
                  });
                  break;
                  
              case 'application/pdf': // PDF case
                  uploadResult = await vectorVault.uploadPdf(
                      file, 
                      Ampersandposium,
                      { 
                          summarize: true,
                      }
                  );
                  break;
                  
              default:
                  setIsLoading(false);
                  alert("Unsupported file type.");
                  return;
          }
          
          log('Upload result:', uploadResult);
          
          // Display success message
          if (file.type === 'application/pdf') {
              alert(`PDF processed successfully. Added ${uploadResult.items_added || 'multiple'} items to your vault.`);
          } else if (file.type.startsWith('image/')) {
              alert("Text successfully extracted.");
          } else {
              alert("File uploaded successfully.");
          }
          
      } catch (error) {
          console.error("Error uploading file:", error);
          alert(`Error uploading file: ${error.message || 'Unknown error'}`);
      } finally {
          event.target.value = null; // Reset the file input
          setIsLoading(false); // Hide spinner
          setReloadData(prevState => !prevState); // Reload data
      }
    }

  // Update handleSiteUpload to accept siteUrl as a parameter
  async function handleSiteUpload(siteUrl) {
    setIsLoading(true);
    await vectorVault.addSite({ vault: Ampersandposium, site: siteUrl, name: siteUrl })
      .then(response => response.json())
      .then(data => {
        log(data);
      })
      .catch(error => {
        console.error("There was an error with the fetch operation:", error);
      })
      .finally(() => {
        setIsLoading(false);
        setReloadData(prevState => !prevState);
        alert("Site uploaded successfully.");
      });
  }




  
  document.addEventListener("touchstart", function() {
    if (document.body.style.zoom !== "1") {
      document.body.style.zoom = 1;
    }
  });

  return (
    <div style={styles.wrapper}>
        <div className="App" style={{...styles.body, paddingTop: "50px"}}>
          <div style={{marginTop:"20px"}}>
            {display && (
              <>
                {!showDelete && ( <> <div style={{marginTop: "-40px"}}></div> </>)}
                {showDelete && (
                  <>
                  <div style={{marginTop: "-10px"}}></div>
                    {database && (
                      <>
                      <div style={{ marginTop: '15px' }}></div>
                                             <VaultSelector
                         currentVault={Ampersandposium}
                         onVaultChange={setAmpersandposium}
                         userEmail={userEmail}
                         styles={styles}
                         isDarkMode={isDarkMode}
                         showDeleteButton={true}
                         showVaultLabel={true}
                         wait={wait}
                         showSearch={true}
                         vaults={vaults}
                         setVaults={setVaults}
                       />
              
                    <div style={{ marginTop: "20px" }}>
                      <AddToVaultSection 
                        isDarkMode={isDarkMode}
                        handleFileUpload={handleFileUpload}
                        handleSiteUpload={handleSiteUpload}
                        handleCustomDataSave={handleCustomDataSave}
                        isMobile={isMobile}
                        styles={styles}
                        setReloadData={setReloadData}
                      />
                    </div>

                  </>
                )}
                {/* Loading Spinner */}
                <div className="loading-spinner" style={{ display: isLoading ? 'block' : 'none' }}>
                  <svg viewBox="0 0 50 50">
                    <circle cx="25" cy="25" r="20" stroke="#00d67a" strokeWidth="5" fill="none" />
                  </svg>
                </div>
                <div>
                    {customDataVisible && (
                      <>
                        <textarea
                            placeholder={`Type or paste something you want to add to the database...`}
                            id="chat-message"
                            rows={isMobile ? 12 : 7}
                            style={{ ...styles.textarea, marginTop: "25px", paddingRight: "6px", paddingLeft: "8px"}}
                            value={customData}
                            onChange={(e) => setCustomData(e.target.value)}
                        />
                        <button style={{ ...styles.button, marginTop: "0px", marginLeft: "0px" }} onClick={() => handleCustomDataSave(customData)}>Save to database </button>
                      </>
                    )}
                    </div>
                    <br />
                  </>
                )}
              </>
            )}
        </div>
      {notificationMessage && (
        <div style={{marginTop: "10px"}}>
        <Notification
          message={notificationMessage}
          notificationShown={notificationShown}
          setNotificationShown={setNotificationShown}
        />
        </div>
      )}

      <DataContainer
        isDarkMode={isDarkMode}
        ref={ChatContainerRef} // pass the reference as a prop
        isMobile={isMobile}
        chatHistory={chatHistory}
        setChatHistory={setChatHistory}
        windowWidth={windowWidth}
        Ampersandposium={Ampersandposium} // vault
        display={display}
        reloadData={reloadData}
        setReloadData={setReloadData}
        showDelete={showDelete}
        userEmail={userEmail} // email
        wait={wait}
        styles={styles}
      />
    </div>
  </div>
  );
}

export default Database;



